import React, { FC } from "react";
import { Box, Stack, Typography } from "@mui/material";

import ServiceIcon from "@components/ServiceIcon";
import BaseButton from "@components/Buttons/base";
import { Colors } from "@src/gatsby-theme-material-ui-top-layout/colors";
import { Colors as ColorsTwentyFive } from "@src/gatsby-theme-material-ui-top-layout/colors-twentyfive";
import { addParameters, consoleSignupURL } from "@src/urls";
import { UtmParameters } from "@src/helpers/utms";
import { uiContainerTransitionMs, uiOpacityHover } from "@src/utils";

interface Props {
  title: string;
  service: string;
  subservice?: string;
  description?: string;
  ctaUrlPrimary?: string;
  ctaLabelPrimary?: string;
  ctaUrlSecondary?: string;
  ctaLabelSecondary?: string;
  ctaPrimaryUtms?: UtmParameters;
  ctaSecondaryUtms?: UtmParameters;

  ctaUrlTertiary?: string;
  ctaLabelTertiary?: string;
}

const ServiceHero: FC<Props> = ({
  title,
  service,
  subservice,
  description,
  ctaUrlPrimary,
  ctaLabelPrimary,
  ctaUrlSecondary,
  ctaLabelSecondary,
  ctaUrlTertiary = "",
  ctaLabelTertiary = "",
  ctaPrimaryUtms = {},
  ctaSecondaryUtms = {},
}) => {
  let primaryUrl = "";
  let secondaryUrl = "";

  if (ctaUrlPrimary === "signup") {
    primaryUrl = addParameters(consoleSignupURL, ctaPrimaryUtms);
  } else {
    primaryUrl = addParameters(ctaUrlPrimary as string, ctaPrimaryUtms);
  }

  if (ctaUrlSecondary && ctaUrlSecondary?.trim() !== "") {
    secondaryUrl = addParameters(ctaUrlSecondary as string, ctaSecondaryUtms);
  }

  return (
    <Stack
      alignItems="center"
      sx={{
        width: "100%",
        marginTop: "-68px",
        marginBottom: "-40px",
        paddingTop: "68px",
        backgroundImage:
          "linear-gradient(95.58deg, #35124D 0%, #3B1556 16.04%, #3D195F 35.62%, #281A52 55.93%, #111E48 76.22%, #195888 100%)",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        overflow: "hidden",
      }}
    >
      <Stack
        maxWidth="xl"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
        sx={{
          position: "relative",
          width: "100%",
          minHeight: "334px",
          padding: "0 16px",
          zIndex: "2",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "-68px",
            right: { xs: "0", md: "-1720px", lg: "-1600px" },
            width: "2782px",
            height: "400px",
            zIndex: "1",
            pointerEvents: "none",
            backgroundImage: {
              xs: "none",
              md: "url(/backgrounds/service-hero-wave.svg)",
            },
            backgroundRepeat: "no-repeat",
          }}
        />
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          spacing={1.5}
          sx={{
            position: "relative",
            color: "white",
            marginBottom: "20px",
            zIndex: "5",
          }}
        >
          <ServiceIcon
            service={service}
            size={44}
            strokeColor={Colors.PrimaryMain}
          />
          <Stack direction="row">
            <Typography variant="h1" sx={{ color: Colors.PrimaryContrast }}>
              {title}
            </Typography>
            {subservice && (
              <>
                <Typography
                  variant="h1"
                  sx={{
                    margin: "0 12px",
                    color: Colors.InfoMain,
                    fontWeight: 300,
                  }}
                >
                  |
                </Typography>
                <Typography variant="h1" sx={{ color: Colors.PrimaryContrast }}>
                  {subservice}
                </Typography>
              </>
            )}
          </Stack>
        </Stack>
        <Typography
          variant="h6"
          sx={{
            maxWidth: {
              xs: "480px",
              md: "600px",
            },
            marginBottom: "20px",
            color: Colors.PrimaryContrast,
          }}
        >
          {description}
        </Typography>

        {ctaUrlTertiary && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            gap={2}
            sx={{
              marginBottom: "24px",
              Button: {
                color: `${ColorsTwentyFive.PrimaryContrast} !important`,
                background: ColorsTwentyFive.PrimaryGradient,
                opacity: 1,
                transition: `opacity ${uiContainerTransitionMs}ms ease-in-out`,
                "&:hover": {
                  opacity: uiOpacityHover + 0.3,
                },
                "*": {
                  color: ColorsTwentyFive.PrimaryContrast,
                },
              },
            }}
          >
            <BaseButton
              url={ctaUrlTertiary}
              variant="contained"
              testId="hero-cta-tertiary"
            >
              {ctaLabelTertiary}
            </BaseButton>
          </Stack>
        )}

        <Stack
          direction={{ xs: "column", sm: "row" }}
          gap={2}
          sx={{ marginBottom: "80px" }}
        >
          {primaryUrl && (
            <BaseButton
              url={primaryUrl}
              variant="contained"
              testId="hero-cta-primary"
            >
              {ctaLabelPrimary}
            </BaseButton>
          )}
          {ctaUrlSecondary && (
            <BaseButton
              url={secondaryUrl}
              variant="text"
              testId="hero-cta-secondary"
            >
              {ctaLabelSecondary}
            </BaseButton>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default ServiceHero;
